import { useEffect, useState } from 'react';

import { Analytics } from '@watershed/analytics/analyticsUtils';
import { maybeNotifySentry } from '@watershed/shared-frontend/utils/errorUtils';
import { clearPinnedOrganizationId } from '@watershed/shared-frontend/utils/pinnedOrganizationId';
import isValidEmail from '@watershed/shared-universal/utils/isValidEmail';

import {
  ErrorState,
  LoginMethodSelectionState,
  LoginPageState,
  LoginState,
  OrgMetadata,
} from '../types';
import { fetchOrgMetadataByEmail } from '../utils/fetchUtils';
import { AuthError } from '@watershed/shared-universal/authConstants';
const EMAIL_LOCALSTORAGE_KEY = 'loginEmail';

interface UseLoginStateParams {
  authError: string | null;
  emailFromQuery: string | null;
  customerCompanyId: string | null;
  recipientCompanyId: string | null;
}

interface UseLoginStateReturn {
  state: LoginPageState;
  setState: React.Dispatch<React.SetStateAction<LoginPageState>>;
  fetchEmailMetadataAndSetState: (
    email: string,
    error: string | undefined
  ) => Promise<void>;
  metadata: OrgMetadata | undefined;
  customerName: string | undefined;
}

export function useLoginState({
  authError,
  emailFromQuery,
  customerCompanyId,
  recipientCompanyId,
}: UseLoginStateParams): UseLoginStateReturn {
  const email =
    emailFromQuery ||
    (typeof window !== 'undefined'
      ? localStorage?.getItem(EMAIL_LOCALSTORAGE_KEY)
      : null) ||
    undefined;

  // The initial error state is the one from the URL, which is likely an enum value, and if it's not we map it to our fallback
  let errorMessage: AuthError | undefined = undefined;
  if (authError) {
    errorMessage = Object.values(AuthError).includes(authError as AuthError)
      ? (authError as AuthError)
      : AuthError.Unknown;
  }

  // The error state is the raw string or the enum value, and we'll localize it when rendering the error message.
  const [state, setState] = useState<LoginPageState>({
    state: LoginState.Email,
    email,
    fetching: false,
    error: errorMessage,
  });

  /**
   * Reset our state if we have a mismatch between the email param in
   * the URL and our internal state.
   */
  useEffect(() => {
    if (state.email !== email) {
      setState({
        state: LoginState.Email,
        email,
        fetching: false,
        error: state.error,
      });
    }
  }, [state.email, email, state.error]);

  const fetchEmailMetadataAndSetState = async (
    email: string,
    error: string | undefined
  ) => {
    setState({
      state: LoginState.Email,
      email,
      fetching: true,
      error,
    });

    let pendingState: LoginMethodSelectionState | ErrorState = {
      state: LoginState.LoginMethodSelection,
      email,
      fetching: true,
      error,
    };

    try {
      const metadata = await fetchOrgMetadataByEmail(email, {
        customerCompanyId,
        recipientCompanyId,
      });
      // Some methods of logging a user out (cookie expiration, admin session revocation)
      // don't clear the user's session storage, so we should do it on login to be sure.
      clearPinnedOrganizationId({ shouldReload: false });

      pendingState = {
        state: LoginState.LoginMethodSelection,
        email,
        fetching: false,
        metadata,
        error,
      };
    } catch (ex) {
      const error = ex as Error;
      maybeNotifySentry(error);
      pendingState = {
        state: LoginState.Error,
        email,
        error: error.message,
      };
      Analytics.error('fetchEmailMetadata', {
        error: error.message,
      });
    }

    setState(pendingState);
  };

  /**
   * If email is already set (whether via url or local storage), automatically
   * kick off the email step.
   */
  useEffect(() => {
    if (email && isValidEmail(email)) {
      void fetchEmailMetadataAndSetState(email, state.error);
    }
    // This is explicitly leaving out `state.error`, because when it changes it
    // can cause rerenders that we don't want. This is the downfall of hooks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const metadata =
    state.state === 'error' || state.state === 'email'
      ? undefined
      : state.metadata;
  const customerName = metadata?.customerName ?? undefined;

  return {
    state,
    setState,
    fetchEmailMetadataAndSetState,
    metadata,
    customerName,
  };
}
